import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Overlay, Tooltip } from 'react-bootstrap'

interface ClampWithTooltipProps {
    text?: string | null
    className?: string
    style?: React.CSSProperties
}

const HIDE_DELAY_MS = 120

export function ClampWithTooltip({ text, className = '', style }: ClampWithTooltipProps) {
    const tooltipId = useId().replace(/:/g, '')
    const textRef = useRef<HTMLSpanElement>(null)
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [isTruncated, setIsTruncated] = useState(false)
    const [showTooltip, setShowTooltip] = useState(false)
    const value = text?.trim() ?? ''

    const clearHideTimer = useCallback(() => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current)
            hideTimerRef.current = null
        }
    }, [])

    const openTooltip = useCallback(() => {
        clearHideTimer()
        if (isTruncated) setShowTooltip(true)
    }, [clearHideTimer, isTruncated])

    const scheduleCloseTooltip = useCallback(() => {
        clearHideTimer()
        hideTimerRef.current = setTimeout(() => setShowTooltip(false), HIDE_DELAY_MS)
    }, [clearHideTimer])

    const checkTruncation = useCallback(() => {
        const el = textRef.current
        if (!el) return
        setIsTruncated(el.scrollWidth > el.clientWidth)
    }, [value])

    useEffect(() => {
        checkTruncation()
        const el = textRef.current
        if (!el || typeof ResizeObserver === 'undefined') return

        const observer = new ResizeObserver(checkTruncation)
        observer.observe(el)
        return () => observer.disconnect()
    }, [checkTruncation])

    useEffect(() => {
        if (!isTruncated) setShowTooltip(false)
    }, [isTruncated])

    useEffect(() => () => clearHideTimer(), [clearHideTimer])

    if (!value) return null

    return (
        <>
            <span
                ref={textRef}
                className={`cw-clamp-text ${className}`.trim()}
                style={style}
                tabIndex={isTruncated ? 0 : undefined}
                onMouseEnter={openTooltip}
                onMouseLeave={scheduleCloseTooltip}
                onFocus={openTooltip}
                onBlur={scheduleCloseTooltip}
            >
                {value}
            </span>
            <Overlay
                target={textRef}
                show={showTooltip && isTruncated}
                placement="top"
                flip
            >
                {(overlayProps) => (
                    <Tooltip
                        {...overlayProps}
                        id={`cw-clamp-tooltip-${tooltipId}`}
                        className="toggle-switch-tooltip cw-clamp-tooltip"
                        onMouseEnter={openTooltip}
                        onMouseLeave={scheduleCloseTooltip}
                    >
                        <span className="cw-clamp-tooltip-text">{value}</span>
                    </Tooltip>
                )}
            </Overlay>
        </>
    )
}
